import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../lib/api';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used inside PlayerProvider');
  return ctx;
};

export const PlayerProvider = ({ children }) => {
  const [queue, setQueue] = useState([]); // array of songs
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // audio-only by default
  const [shuffle, setShuffle] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // 'off' | 'all' | 'one'
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoAvailable, setVideoAvailable] = useState(true); // for iOS background fallback
  const playerRef = useRef(null);

  const currentSong = currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;

  const playQueue = useCallback((songs, startIndex = 0) => {
    if (!songs || songs.length === 0) return;
    setQueue(songs);
    setCurrentIndex(startIndex);
    setIsPlaying(true);
    setIsExpanded(true);
  }, []);

  const playSong = useCallback((song) => {
    setQueue([song]);
    setCurrentIndex(0);
    setIsPlaying(true);
    setIsExpanded(true);
  }, []);

  const playRandom = useCallback(async ({ expand = false } = {}) => {
    try {
      const { data } = await api.get('/random-song');
      if (!data) return;
      setQueue([data]);
      setCurrentIndex(0);
      setIsPlaying(true);
      if (expand) setIsExpanded(true);
      return data;
    } catch (e) { /* noop */ }
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const next = useCallback(async () => {
    // Single-song queue (autoplay-random path): fetch another random rather than being a no-op.
    if (queue.length <= 1) {
      try {
        const { data } = await api.get('/random-song');
        if (data) {
          setQueue([data]);
          setCurrentIndex(0);
          setIsPlaying(true);
        }
      } catch (e) { /* noop */ }
      return;
    }
    setCurrentIndex((idx) => {
      if (queue.length === 0) return -1;
      if (shuffle) {
        let n = Math.floor(Math.random() * queue.length);
        if (n === idx && queue.length > 1) n = (n + 1) % queue.length;
        return n;
      }
      if (idx + 1 >= queue.length) {
        return repeatMode === 'all' ? 0 : idx;
      }
      return idx + 1;
    });
    setIsPlaying(true);
  }, [queue, shuffle, repeatMode]);

  const prev = useCallback(async () => {
    // Single-song queue: same graceful behaviour — pull a fresh random.
    if (queue.length <= 1) {
      try {
        const { data } = await api.get('/random-song');
        if (data) {
          setQueue([data]);
          setCurrentIndex(0);
          setIsPlaying(true);
        }
      } catch (e) { /* noop */ }
      return;
    }
    setCurrentIndex((idx) => (idx <= 0 ? (repeatMode === 'all' ? queue.length - 1 : 0) : idx - 1));
    setIsPlaying(true);
  }, [queue, repeatMode]);

  const seekTo = useCallback((seconds) => {
    if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(seconds, true);
      setCurrentTime(seconds);
    }
  }, []);

  const toggleShuffle = useCallback(() => setShuffle((s) => !s), []);
  const cycleRepeat = useCallback(() => {
    setRepeatMode((m) => (m === 'off' ? 'all' : m === 'all' ? 'one' : 'off'));
  }, []);

  const expand = useCallback(() => setIsExpanded(true), []);
  const minimize = useCallback(() => setIsExpanded(false), []);
  const toggleShowVideo = useCallback(() => setShowVideo((v) => !v), []);

  const onSongEnd = useCallback(() => {
    if (repeatMode === 'one') {
      if (playerRef.current) {
        playerRef.current.seekTo(0, true);
        playerRef.current.playVideo();
      }
      return;
    }
    next();
  }, [repeatMode, next]);

  // Media Session API
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentSong) {
      try { navigator.mediaSession.metadata = null; } catch (e) {}
      return;
    }
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: currentSong.title,
      artist: currentSong.artist,
      album: 'Patna Ke Presleys',
      artwork: [
        { src: currentSong.thumbnail_url, sizes: '512x512', type: 'image/jpeg' },
      ],
    });
    const setHandler = (action, fn) => {
      try { navigator.mediaSession.setActionHandler(action, fn); } catch (e) {}
    };
    setHandler('play', () => setIsPlaying(true));
    setHandler('pause', () => setIsPlaying(false));
    setHandler('previoustrack', () => prev());
    setHandler('nexttrack', () => next());
    setHandler('seekto', (details) => {
      if (details.seekTime != null) seekTo(details.seekTime);
    });
  }, [currentSong, next, prev, seekTo]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // Detect visibility change for iOS video fallback
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        // Backgrounded: keep audio, drop visual (video will pause on iOS Safari anyway;
        // Media Session ensures OS controls still work).
        setVideoAvailable(false);
      } else {
        setVideoAvailable(true);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const value = {
    queue, currentIndex, currentSong, isPlaying, isExpanded, showVideo,
    shuffle, repeatMode, currentTime, duration, videoAvailable,
    playerRef, playQueue, playSong, playRandom, togglePlay, next, prev,
    seekTo, toggleShuffle, cycleRepeat, expand, minimize, toggleShowVideo,
    onSongEnd, setCurrentTime, setDuration, setIsPlaying,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};
